import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import ClientAppShell from "../components/ClientAppShell";
import { CapSelectionCheckbox } from "../components/client/CapSelectionCheckbox";
import { usePageWalkthrough } from "../hooks/usePageWalkthrough";
import { submitCapabilities } from "../services/capabilitiesApi";
import {
  loadProjectCapabilities,
  loadSelectedCapabilities,
} from "../services/clientProjectData";
import { fetchRequestStatus as fetchRequestStatusAPI, SubmitCapabilityRequest } from "../services/assessment";
import { useAuth } from "../auth/AuthContext";
import { useMenu } from "../Common/MenuContext";
import CapabilityAccessBanner from "./components/CapabilityAccessBanner";
import ContentLoader from "../components/ContentLoader";
import { showAlert } from "../components/AppAlert/appAlert";
import {
  assessmentUnitKey,
  isLeafCapability,
  sameAssessmentUnit,
  selectableUnitCount,
} from "../utils/assessmentUnitUtils";
import "../assets/css/capablities.css";

const Capabilities = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshChatCount } = useMenu();

  const [visibleSection, setVisibleSection] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);
  const [baselineSelectionKeys, setBaselineSelectionKeys] = useState([]);
  const [selectedCounts, setSelectedCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [requestStatus, setRequestStatus] = useState("Not Requested");
  const [capabilitiesState, setCapabilitiesState] = useState({
    allSubmitted: false,
    userHasRequested: false,
    userCanAddNewCapabilities: false,
    disableAllSelection: false
  });
  const [isActive, setIsActive] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const getSelectionKey = (cap) =>
    assessmentUnitKey(cap.capabilityId, cap.subCapabilityId);

  const toSortedSelectionKeys = (caps) =>
    caps.map(getSelectionKey).sort();

  const areSelectionKeysEqual = (keysA, keysB) => {
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key, index) => key === keysB[index]);
  };

  const { walkthrough } = usePageWalkthrough({
    userId,
    parentId: 2,
    targetMap: {
      '[data-nav="capabilities"]': '[data-nav="capabilities"]',
      '[data-element="capability-category"]:first': '[data-element="capability-category"]',
      '[data-action="next"]': '[data-action="next"]',
    },
  });

  const handleMenuSelection = (item) => {
    setSelectedItem(item);
  };

  useEffect(() => {
    const storedProjectId = localStorage.getItem("projectId");
    if (storedProjectId) {
      setProjectId(parseInt(storedProjectId, 10));
    }
  }, []);

  useEffect(() => {
    if (user?.userId) {
      setUserId(user.userId);
    }
  }, [user]);

  useEffect(() => {
    if (!projectId || !userId) return;

    const fetchData = async () => {
      try {
        const capabilitiesResponse = await loadProjectCapabilities(projectId);
        setCapabilities(capabilitiesResponse.data);

        const selectedResponse = await loadSelectedCapabilities({
          userId,
          projectId,
        });

        const preselected =
          selectedResponse?.data?.selectedCapabilities?.flatMap((cap) =>
            cap.selectedSubCapabilities.map((subCap) => ({
              capabilityId: cap.capabilityId,
              subCapabilityId: subCap.subCapabilityId,
              state: subCap.state || "3",
              isNewLocked: subCap.isNewLocked ?? false,
              isApproved: subCap.state === "4",
              isNewlySelected: false,
            }))
          ) || [];

        setSelectedCapabilities(preselected);
        setBaselineSelectionKeys(toSortedSelectionKeys(preselected));

        const allSubmitted =
          preselected.length > 0 &&
          preselected.every((cap) => cap.state === "4");

        let requestResponse;
        try {
          requestResponse = await fetchRequestStatusAPI({ userId, projectId });
        } catch (error) {
          requestResponse = { data: null };
        }

        const requestStatus = requestResponse?.data?.status || "Not Requested";
        let isActive = requestResponse?.data?.isActive ?? false;

        const isNewUser =
          preselected.length === 0 && requestStatus === "Not Requested";

        if (requestStatus === "Approved") {
          isActive = 1;
        } else if (allSubmitted) {
          isActive = 0;
        }

        setRequestStatus(requestStatus);
        setIsActive(isActive);

        setCapabilitiesState({
          allSubmitted,
          userCanAddNewCapabilities:
            isNewUser || (requestStatus === "Approved" && isActive),
          disableAllSelection:
            !isNewUser && allSubmitted && requestStatus !== "Approved",
        });

        setSelectedCounts(
          preselected.reduce((counts, item) => {
            counts[item.capabilityId] = (counts[item.capabilityId] || 0) + 1;
            return counts;
          }, {})
        );
      } catch (error) {
        console.error("Error fetching capabilities:", error);
        setError("Failed to load capabilities data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, userId]);

  const toggleVisibility = (sectionId) => {
    setVisibleSection((prev) => {
      const next = prev === sectionId ? null : sectionId;
      setSelectedCard(next);
      return next;
    });
  };

  const handleCheckboxChange = (e, capabilityId, subCapabilityId) => {
    const isChecked = e.target.checked;
    
    const isApproved = selectedCapabilities.some(
      (item) => 
        sameAssessmentUnit(item.capabilityId, item.subCapabilityId, capabilityId, subCapabilityId) &&
        (item.isApproved || item.state === "4")
    );
    
    const isLocked = selectedCapabilities.some(
      (item) => 
        sameAssessmentUnit(item.capabilityId, item.subCapabilityId, capabilityId, subCapabilityId) &&
        item.isNewLocked &&
        requestStatus !== "Not Requested"
    );
    
    if (isApproved || isLocked || (isActive === 0 && !capabilitiesState.userCanAddNewCapabilities) || capabilitiesState.disableAllSelection) {
      return;
    }
    
    setSelectedCapabilities((prev) => {
      const existingIndex = prev.findIndex(
        (item) =>
          sameAssessmentUnit(item.capabilityId, item.subCapabilityId, capabilityId, subCapabilityId)
      );
      
      if (existingIndex !== -1) {
        if (!isChecked) {
          const existingItem = prev[existingIndex];
          if (!existingItem.isApproved && !existingItem.isNewLocked) {
            return prev.filter((_, index) => index !== existingIndex);
          }
          return prev;
        }
        return prev;
      } else if (isChecked) {
        return [
          ...prev,
          {
            capabilityId,
            subCapabilityId,
            state: "3",
            isNewLocked: false,
            isApproved: false,
            isNewlySelected: true,
          },
        ];
      }
      
      return prev;
    });

    setSelectedCounts((prevCounts) => {
      const updatedCounts = { ...prevCounts };
      const currentCount = updatedCounts[capabilityId] || 0;
      
      if (isChecked) {
        updatedCounts[capabilityId] = currentCount + 1;
      } else {
        const isRemovable = !selectedCapabilities.some(
          (item) => 
            sameAssessmentUnit(item.capabilityId, item.subCapabilityId, capabilityId, subCapabilityId) &&
            (item.isApproved || item.isNewLocked)
        );
        
        if (isRemovable) {
          updatedCounts[capabilityId] = Math.max(currentCount - 1, 0);
        }
      }
      
      return updatedCounts;
    });
  };

  const canGoToAssessment =
    (requestStatus === "Approved" && isActive === 1) ||
    requestStatus === "Not Requested";

  const currentSelectionKeys = toSortedSelectionKeys(selectedCapabilities);
  const hasSelectionChanged = !areSelectionKeysEqual(
    currentSelectionKeys,
    baselineSelectionKeys
  );
  const canSubmitSelectionChanges =
    hasSelectionChanged && selectedCapabilities.length > 0;

  // Assessment already finished and advisor has not re-unlocked selection.
  const isAssessmentSelectionLocked =
    Boolean(capabilitiesState.allSubmitted) &&
    !(requestStatus === "Approved" && Boolean(isActive));

  const notifySelectionLocked = () => {
    if (!isAssessmentSelectionLocked) return false;
    showAlert({
      type: "warning",
      title: "Selection locked",
      message:
        "Selection of new capabilities is locked because your assessment is already complete. Request more capabilities from your advisor to unlock additional areas.",
    });
    return true;
  };

  const isNextDisabled =
    !canGoToAssessment ||
    !canSubmitSelectionChanges ||
    capabilitiesState.disableAllSelection ||
    (capabilitiesState.allSubmitted && requestStatus !== "Approved");

  const handleSubmit = async () => {
    try {
      if (!projectId) {
        showAlert("Project ID is not available, so we can't submit right now.", "error");
        return false;
      }

      if (selectedCapabilities.length === 0) {
        showAlert("Select at least one capability before continuing.", "warning");
        return false;
      }

      if (!hasSelectionChanged) {
        showAlert("Update your selection before continuing.", "warning");
        return false;
      }

      // Persist the full current selection so both adds and removals are applied.
      const updatedCapabilities = selectedCapabilities.map((cap) =>
        cap.isNewlySelected ? { ...cap, state: "3" } : cap
      );

      const payload = {
        userId,
        projectId,
        selectedCapabilities: updatedCapabilities,
      };

      await submitCapabilities(payload);

      // Update the local state to reflect the submission
      setSelectedCapabilities(updatedCapabilities.map(cap => ({
        ...cap,
        isNewlySelected: false // Once submitted, they're no longer newly selected
      })));
      setBaselineSelectionKeys(toSortedSelectionKeys(updatedCapabilities));

      // Refetch the data to get the latest state
      const selectedResponse = await loadSelectedCapabilities({
        userId,
        projectId,
      });
      
      if (selectedResponse?.data?.selectedCapabilities) {
        const refreshedCapabilities = selectedResponse.data.selectedCapabilities.flatMap((cap) =>
          cap.selectedSubCapabilities.map((subCap) => ({
            capabilityId: cap.capabilityId,
            subCapabilityId: subCap.subCapabilityId,
            state: subCap.state || "3",
            isNewLocked: subCap.isNewLocked ?? false,
            isApproved: subCap.state === "4",
            isNewlySelected: false,
          }))
        );
        
        setSelectedCapabilities(refreshedCapabilities);
        setBaselineSelectionKeys(toSortedSelectionKeys(refreshedCapabilities));
        
        // Update the counts
        setSelectedCounts(
          refreshedCapabilities.reduce((counts, item) => {
            counts[item.capabilityId] = (counts[item.capabilityId] || 0) + 1;
            return counts;
          }, {})
        );
      }

      refreshChatCount();

      return true;
    } catch (error) {
      console.error("Error submitting capabilities:", error);
      showAlert("There was an error submitting your capabilities. Please try again.", "error");
      return false;
    }
  };

  const handleNextClick = async () => {
    if (!canGoToAssessment || isSubmitting) return;

    setIsSubmitting(true);
    const success = await handleSubmit();
    if (success) {
      navigate("/assessment");
      return;
    }
    setIsSubmitting(false);
  };

  // Allow requesting from every state except when a request is already in
  // flight (Pending) or capabilities are currently unlocked (Approved+active).
  // This covers Not Requested, Rejected, Approved-but-inactive and Processed.
  const canRequestMore = !(
    requestStatus === "Pending" || (requestStatus === "Approved" && isActive)
  );

  const handleRequestCapabilities = async () => {
    if (requesting || !canRequestMore) return;

    if (!userId || !projectId) {
      showAlert("We couldn't identify your project. Please reopen it and try again.", "error");
      return;
    }

    try {
      setRequesting(true);
      const response = await SubmitCapabilityRequest({ userId, projectId, status: "Pending" });
      if (response && response.data) {
        setRequestStatus("Pending");
        showAlert("Your request to add capabilities has been sent to your advisor.", "success");
      } else {
        showAlert("We couldn't submit your request. Please try again.", "error");
      }
    } catch (error) {
      showAlert("We couldn't submit your request. Please try again.", "error");
    } finally {
      setRequesting(false);
    }
  };

  const totalAreasSelected = selectedCapabilities.length;

  const getGroupSelectionState = (capability) => {
    const total = selectableUnitCount(capability);
    const selected = selectedCounts[capability.id] || 0;
    if (selected <= 0) return "none";
    if (selected >= total) return "all";
    return "partial";
  };

  const handleSelectAllSubs = (capability) => {
    (capability.subCapabilities ?? []).forEach((sub) => {
      const isSelected = selectedCapabilities.some((item) =>
        sameAssessmentUnit(
          item.capabilityId,
          item.subCapabilityId,
          capability.id,
          sub.subId
        )
      );
      if (isSelected) return;

      const isSubDisabled =
        (isActive === 0 && !capabilitiesState.userCanAddNewCapabilities) ||
        capabilitiesState.disableAllSelection;

      if (!isSubDisabled) {
        handleCheckboxChange({ target: { checked: true } }, capability.id, sub.subId);
      }
    });
  };

  const handleParentGroupCheckbox = (capability) => {
    const state = getGroupSelectionState(capability);
    if (state === "all") {
      (capability.subCapabilities ?? []).forEach((sub) => {
        const isSelected = selectedCapabilities.some((item) =>
          sameAssessmentUnit(
            item.capabilityId,
            item.subCapabilityId,
            capability.id,
            sub.subId
          )
        );
        if (!isSelected) return;

        const isSubApproved = selectedCapabilities.some(
          (item) =>
            sameAssessmentUnit(
              item.capabilityId,
              item.subCapabilityId,
              capability.id,
              sub.subId
            ) &&
            (item.isApproved || item.state === "4")
        );
        const isSubLocked = selectedCapabilities.some(
          (item) =>
            sameAssessmentUnit(
              item.capabilityId,
              item.subCapabilityId,
              capability.id,
              sub.subId
            ) &&
            item.isNewLocked &&
            requestStatus !== "Not Requested"
        );

        if (!isSubApproved && !isSubLocked) {
          handleCheckboxChange(
            { target: { checked: false } },
            capability.id,
            sub.subId
          );
        }
      });
      return;
    }
    handleSelectAllSubs(capability);
  };

  let accessVariant: "unlocked" | "pending" | "rejected" | "locked" | null = null;
  if (capabilitiesState.allSubmitted) {
    if (requestStatus === "Approved" && isActive) {
      accessVariant = "unlocked";
    } else if (requestStatus === "Pending") {
      accessVariant = "pending";
    } else if (requestStatus === "Rejected") {
      accessVariant = "rejected";
    } else {
      accessVariant = "locked";
    }
  }

  return (
    <>
      {walkthrough}
      <ClientAppShell
        title="Capabilities"
        dataNav="capabilities"
        selectedItem={selectedItem}
        onMenuSelect={handleMenuSelection}
        containerClassName="cap-selection-page"
      >
        <div className="cap-sel">
          {loading ? (
            <ContentLoader page label="Loading capabilities…" />
          ) : error ? (
            <div className="cap-error">{error}</div>
          ) : isSubmitting ? (
            <ContentLoader label="Saving your selections…" />
          ) : (
            <>
              {accessVariant && (
                <div className="cap-access-wrap">
                  <CapabilityAccessBanner
                    variant={accessVariant}
                    onRequest={handleRequestCapabilities}
                    requesting={requesting}
                  />
                </div>
              )}

              <header className="cap-sel-header">
                <p className="cap-sel-step">Step 1 of 4</p>
                <h2 className="cap-sel-title">Choose what to assess</h2>
                <p className="cap-sel-lead">
                  Select one or more. Some capabilities have sub-capabilities you can pick
                  individually.
                </p>
              </header>

              <div className="cap-sel-scroll">
              <ul className="cap-sel-list" role="list">
                {capabilities.map((capability) => {
                  const leaf = isLeafCapability(capability);
                  const subCount = (capability.subCapabilities ?? []).length;
                  const groupState = getGroupSelectionState(capability);
                  const groupSelectedCount = selectedCounts[capability.id] || 0;
                  const isExpanded = visibleSection === capability.id;

                  const leafSelected = selectedCapabilities.some((item) =>
                    sameAssessmentUnit(
                      item.capabilityId,
                      item.subCapabilityId,
                      capability.id,
                      null
                    )
                  );
                  const isApproved = selectedCapabilities.some(
                    (item) =>
                      sameAssessmentUnit(
                        item.capabilityId,
                        item.subCapabilityId,
                        capability.id,
                        null
                      ) &&
                      (item.isApproved || item.state === "4")
                  );
                  const isLocked = selectedCapabilities.some(
                    (item) =>
                      sameAssessmentUnit(
                        item.capabilityId,
                        item.subCapabilityId,
                        capability.id,
                        null
                      ) &&
                      item.isNewLocked &&
                      requestStatus !== "Not Requested"
                  );
                  const leafDisabled =
                    isApproved ||
                    isLocked ||
                    (isActive === 0 && !capabilitiesState.userCanAddNewCapabilities) ||
                    capabilitiesState.disableAllSelection;

                  const groupHasSelection = groupSelectedCount > 0;
                  const groupDisabled =
                    (isActive === 0 && !capabilitiesState.userCanAddNewCapabilities) ||
                    capabilitiesState.disableAllSelection;

                  return (
                    <li className="cap-sel-item" key={capability.id}>
                      <article
                        className={[
                          "cap-sel-card",
                          leaf && leafSelected ? "is-selected" : "",
                          !leaf && groupHasSelection ? "is-selected" : "",
                          !leaf && groupState === "partial" ? "is-partial" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        data-element="capability-category"
                      >
                        <div
                          className="cap-sel-card__main"
                          onClick={() => {
                            if (leaf) {
                              if (leafDisabled) {
                                if (!leafSelected) notifySelectionLocked();
                                return;
                              }
                              handleCheckboxChange(
                                { target: { checked: !leafSelected } },
                                capability.id,
                                null
                              );
                              return;
                            }
                            toggleVisibility(capability.id);
                          }}
                          onKeyDown={(e) => {
                            if (leaf && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              if (leafDisabled) {
                                if (!leafSelected) notifySelectionLocked();
                                return;
                              }
                              handleCheckboxChange(
                                { target: { checked: !leafSelected } },
                                capability.id,
                                null
                              );
                            }
                          }}
                          role={leaf ? "button" : undefined}
                          tabIndex={leaf && !leafDisabled ? 0 : undefined}
                        >
                          <CapSelectionCheckbox
                            checked={leaf ? leafSelected : groupState === "all"}
                            indeterminate={!leaf && groupState === "partial"}
                            disabled={leaf ? leafDisabled : groupDisabled}
                            aria-label={`Select ${capability.capabilityName}`}
                            onChange={(e) => {
                              if (leaf) {
                                handleCheckboxChange(e, capability.id, null);
                              } else {
                                handleParentGroupCheckbox(capability);
                              }
                            }}
                          />

                          <div className="cap-sel-card__body">
                            <div className="cap-sel-card__title-row">
                              <h3 className="cap-sel-card__title">
                                {capability.capabilityName}
                              </h3>
                              <span
                                className={
                                  leaf
                                    ? "cap-sel-tag cap-sel-tag--single"
                                    : "cap-sel-tag cap-sel-tag--group"
                                }
                              >
                                {leaf
                                  ? "Single capability"
                                  : `${subCount} sub-capabilit${subCount === 1 ? "y" : "ies"}`}
                              </span>
                            </div>
                            <p className="cap-sel-card__desc">
                              {capability.capabilityDescription}
                            </p>
                          </div>

                          {!leaf ? (
                            <button
                              type="button"
                              className="cap-sel-expand"
                              aria-expanded={isExpanded}
                              aria-label={
                                isExpanded ? "Collapse sub-capabilities" : "Expand sub-capabilities"
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleVisibility(capability.id);
                              }}
                            >
                              <span className="cap-sel-expand__meta">
                                <span className="cap-sel-expand__meta-count">
                                  {groupSelectedCount} of {selectableUnitCount(capability)} selected
                                </span>
                              </span>
                              <span className="cap-sel-expand__icon-wrap" aria-hidden>
                                <ChevronDown
                                  className={`cap-sel-expand__icon${isExpanded ? " is-open" : ""}`}
                                  size={16}
                                />
                              </span>
                            </button>
                          ) : null}
                        </div>

                        {!leaf && isExpanded ? (
                          <div className="cap-sel-subs">
                            <div className="cap-sel-subs__head">
                              <span className="cap-sel-subs__label">Sub-capabilities</span>
                              <button
                                type="button"
                                className="cap-sel-subs__select-all"
                                disabled={groupDisabled}
                                onClick={() => handleSelectAllSubs(capability)}
                              >
                                Select all
                              </button>
                            </div>
                            <div className="cap-sel-subs__grid">
                              {(capability.subCapabilities ?? []).map((subCapability) => {
                                const isSelected = selectedCapabilities.some((item) =>
                                  sameAssessmentUnit(
                                    item.capabilityId,
                                    item.subCapabilityId,
                                    capability.id,
                                    subCapability.subId
                                  )
                                );

                                const isSubApproved = selectedCapabilities.some(
                                  (item) =>
                                    sameAssessmentUnit(
                                      item.capabilityId,
                                      item.subCapabilityId,
                                      capability.id,
                                      subCapability.subId
                                    ) &&
                                    (item.isApproved || item.state === "4")
                                );

                                const isSubLocked = selectedCapabilities.some(
                                  (item) =>
                                    sameAssessmentUnit(
                                      item.capabilityId,
                                      item.subCapabilityId,
                                      capability.id,
                                      subCapability.subId
                                    ) &&
                                    item.isNewLocked &&
                                    requestStatus !== "Not Requested"
                                );

                                const isSubDisabled =
                                  isSubApproved ||
                                  isSubLocked ||
                                  (isActive === 0 &&
                                    !capabilitiesState.userCanAddNewCapabilities) ||
                                  capabilitiesState.disableAllSelection;

                                return (
                                  <div
                                    key={subCapability.subId}
                                    className={[
                                      "cap-sel-sub-card",
                                      isSelected ? "is-selected" : "",
                                      isSubDisabled ? "is-disabled" : "",
                                    ]
                                      .filter(Boolean)
                                      .join(" ")}
                                    role="button"
                                    tabIndex={isSubDisabled ? -1 : 0}
                                    onClick={() => {
                                      if (isSubDisabled) {
                                        if (!isSelected) notifySelectionLocked();
                                        return;
                                      }
                                      handleCheckboxChange(
                                        { target: { checked: !isSelected } },
                                        capability.id,
                                        subCapability.subId
                                      );
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key !== "Enter" && e.key !== " ") return;
                                      e.preventDefault();
                                      if (isSubDisabled) {
                                        if (!isSelected) notifySelectionLocked();
                                        return;
                                      }
                                      handleCheckboxChange(
                                        { target: { checked: !isSelected } },
                                        capability.id,
                                        subCapability.subId
                                      );
                                    }}
                                  >
                                    <CapSelectionCheckbox
                                      checked={isSelected}
                                      disabled={isSubDisabled}
                                      aria-label={`Select ${subCapability.subCapabilityName}`}
                                      onChange={(e) =>
                                        handleCheckboxChange(
                                          e,
                                          capability.id,
                                          subCapability.subId
                                        )
                                      }
                                    />
                                    <div className="cap-sel-sub-card__text">
                                      <div className="cap-sel-sub-card__title">
                                        {subCapability.subCapabilityName}
                                      </div>
                                      <p className="cap-sel-sub-card__desc">
                                        {subCapability.subCapabilityDescription}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}
                      </article>
                    </li>
                  );
                })}
              </ul>
              </div>

              <footer
                className="cap-sel-footer"
                onClick={() => {
                  if (isNextDisabled) notifySelectionLocked();
                }}
              >
                <p className="cap-sel-footer__count">
                  <strong className="cap-sel-footer__count-num">
                    {totalAreasSelected}
                  </strong>
                  {totalAreasSelected === 1 ? " area" : " areas"} selected
                </p>
                <button
                  type="button"
                  onClick={handleNextClick}
                  disabled={isNextDisabled}
                  className="cap-sel-footer__cta app-btn app-btn--primary"
                  id="nextLink"
                  data-action="next"
                  style={
                    isNextDisabled && isAssessmentSelectionLocked
                      ? { pointerEvents: "none" }
                      : undefined
                  }
                >
                  Continue to assessment
                  <ArrowRight size={18} aria-hidden />
                </button>
              </footer>
            </>
          )}
        </div>
      </ClientAppShell>
    </>
  );
};

export default Capabilities;
